import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChapterMenu } from "../Components/ChapterMenu";
import { ContentArea } from "../Components/ContentArea";
import {
  Course,
  ChapterContent,
  ChapterTreeNode,
  ChapterRole,
  ContentType
} from "../Types/CourseTypes";
import {
  GetCourseById,
  GetCourseForUser,
  GetCoursePreview,
  GetCourseProgress,
  MarkChapterCompleted
} from "../DataService/CourseService";
import { useUser } from "../DataService/UserContext";
import "./CoursePlayer.css";

export const CoursePlayer = () => {
  const { courseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();

  const isPreview =
    location.pathname.startsWith("/admin/course-preview/") || user?.roles?.includes("admin");

  const [courseContent, setCourseContent] = useState<Course | null>(null);

  const [chapterTree, setChapterTree] = useState<ChapterTreeNode[]>([]);
  const [expandedChapterIds, setExpandedChapterIds] = useState<number[]>([]);

  const [currentChapterId, setCurrentChapterId] = useState<number | null>(null);
  const [currentContentId, setCurrentContentId] = useState<number | null>(null);
  const [currentContent, setCurrentContent] =
    useState<ChapterContent | null>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* =====================================================
     INIT
  ===================================================== */
  useEffect(() => {
    if (!courseId) return;

    const init = async () => {
      try {
        setLoading(true);

        const progressData = isPreview
          ? await GetCoursePreview(+courseId)
          : await GetCourseProgress(+courseId);

        const content = isPreview
          ? await GetCourseById(+courseId)
          : await GetCourseForUser(+courseId);

        if (!content || !progressData) {
          throw new Error("Missing course data");
        }

        setCourseContent(content);

        // 🌳 Build chapter tree
        const tree: ChapterTreeNode[] = progressData.chapters
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(pc => {
            const chapter = content.chapters.find(
              c => c.id === pc.chapterId
            );

            return {
              chapterId: pc.chapterId,
              title: chapter?.title ?? "Namnlöst kapitel",
              sortOrder: pc.sortOrder,
              chapterRole: chapter?.chapterRole ?? pc.chapterRole ?? ChapterRole.Normal,
              isUnlocked: pc.isUnlocked,
              completedAt: pc.completedAt ?? null,
              contents: chapter
                ? [...chapter.contents]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(ct => ({
                      contentId: ct.id,
                      contentType: ct.contentType,
                      title:
                        ct.contentType === ContentType.Video
                          ? ct.video?.title ?? "Video"
                          : ct.contentType === ContentType.Text
                          ? "Text"
                          : ct.contentType === ContentType.Pdf
                          ? "PDF"
                          : "Bilaga"
                    }))
                : []
            };
          });

        setChapterTree(tree);

        // 🎯 Select start chapter
        const startChapter = isPreview
          ? tree[0]
          : tree.find(c => c.isUnlocked);

        if (!startChapter) {
  setError("Kursen saknar kapitel.");
  setLoading(false);
  return;
}

// Sätt kapitel även om det är låst eller tomt
setCurrentChapterId(startChapter.chapterId);
setExpandedChapterIds([startChapter.chapterId]);

// Content sätts bara om det finns
if (startChapter.contents.length > 0) {
  setCurrentContentId(startChapter.contents[0].contentId);
} else {
  setCurrentContentId(null);
}


        setLoading(false);
      } catch (err) {
        console.error(err);
        setError("Kunde inte ladda kursen.");
        setLoading(false);
      }
    };

    init();
  }, [courseId, isPreview]);

  /* =====================================================
     DERIVE CURRENT CONTENT (SINGLE SOURCE OF TRUTH)
  ===================================================== */
  useEffect(() => {
    if (!courseContent || !currentChapterId || !currentContentId) {
      setCurrentContent(null);
      return;
    }

    const chapter = courseContent.chapters.find(
      c => c.id === currentChapterId
    );

    const content = chapter?.contents.find(
      c => c.id === currentContentId
    );

    setCurrentContent(content ?? null);
  }, [courseContent, currentChapterId, currentContentId]);

  /* =====================================================
     HANDLERS
  ===================================================== */
  const handleToggleChapter = (chapterId: number) => {
    setExpandedChapterIds(prev =>
      prev.includes(chapterId)
        ? prev.filter(id => id !== chapterId)
        : [...prev, chapterId]
    );
  };

  const handleSelectContent = (
    chapterId: number,
    contentId: number
  ) => {
    setCurrentChapterId(chapterId);
    setCurrentContentId(contentId);
    setIsMenuOpen(false);
  };

  const handleChapterComplete = async () => {
    if (isPreview || !courseContent || !currentChapterId) return;

    await MarkChapterCompleted(courseContent.id, currentChapterId);
  };

  /* =====================================================
     RENDER GUARDS
  ===================================================== */
  if (loading) return <div>Laddar kurs...</div>;
  if (error) return <div>{error}</div>;
  if (!courseContent)
    return <div>Laddar kursdata...</div>;

  const completedCount = chapterTree.filter(chapter => chapter.completedAt != null).length;
  const progressPercentage = chapterTree.length > 0
    ? Math.round((completedCount / chapterTree.length) * 100)
    : 0;

  /* =====================================================
     RENDER
  ===================================================== */
  return (
    <div className="coursePlayerRoot">
      {isPreview && (
        <div className="coursePlayerPreviewBanner">
          Förhandsgranskning för admin
        </div>
      )}
      {!isPreview && (
        <div className="coursePlayerMemberBanner">
          <button type="button" className="coursePlayerMemberBack" onClick={() => navigate('/member-home')}>
            ← Till mina kurser
          </button>
          <span>Du är inne i din kursöversikt.</span>
        </div>
      )}
      <div className="coursePlayerMain">
        {/* Mobile Header */}
        <div className="coursePlayerMobileHeader">
          <button
            className="coursePlayerMobileHeaderButton"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            ☰
          </button>
          <h1 className="coursePlayerMobileHeaderTitle">
            {courseContent.title}
          </h1>
        </div>

        {/* Chapter Menu */}
        <div className="coursePlayerMenuWrapper">
          <ChapterMenu
            courseTitle={courseContent.title}
            courseDescription={courseContent.description}
            chapters={chapterTree}
            expandedChapterIds={expandedChapterIds}
            currentContentId={currentContentId}
            onToggleChapter={handleToggleChapter}
            onSelectContent={handleSelectContent}
            isOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            progressPercentage={progressPercentage}
          />
        </div>

        {/* Content Area */}
        <div className="coursePlayerVideoArea">
          <ContentArea
            content={currentContent}
            onContentCompleted={handleChapterComplete}
          />
        </div>
      </div>
    </div>
  );
};
